export type SelectOptionValue = string | number;

export type LabeledOption = {
  label: string;
  value: SelectOptionValue;
};

export type LabeledTreeOption = LabeledOption & {
  children?: readonly LabeledTreeOption[];
};

type TreeOptionWithChildren = {
  children?: readonly unknown[];
};

export function isOptionValue(value: unknown): value is SelectOptionValue {
  return typeof value === 'string' || typeof value === 'number';
}

export function isOptionValueArray(value: unknown): value is SelectOptionValue[] {
  return Array.isArray(value) && value.every(isOptionValue);
}

export function isLeafTreeOption<T extends TreeOptionWithChildren>(option: T | null | undefined): option is T {
  if (!option) return false;

  return !option.children?.length;
}

export function getOptionLabels(options: readonly LabeledOption[], values: readonly SelectOptionValue[]): string[] {
  const labels = new Map(options.map((option) => [option.value, option.label]));

  return values.map((value) => labels.get(value)).filter((label): label is string => Boolean(label));
}

export function getSelectOptionLabel(options: readonly LabeledOption[], value: SelectOptionValue): string {
  return options.find((option) => option.value === value)?.label ?? String(value);
}

export function getSelectedOptionLabel(option: LabeledOption): string {
  const value = String(option.value);

  return option.label.startsWith(`${value} -`) ? value : option.label;
}

function findTreeOptionLabel(options: readonly LabeledTreeOption[], value: SelectOptionValue): string | null {
  for (const option of options) {
    if (option.value === value) return getSelectedOptionLabel(option);

    const childLabel = option.children ? findTreeOptionLabel(option.children, value) : null;
    if (childLabel) return childLabel;
  }

  return null;
}

export function getTreeOptionLabel(options: readonly LabeledTreeOption[], value: SelectOptionValue): string {
  return findTreeOptionLabel(options, value) ?? String(value);
}
